import { Component, Input } from '@angular/core';
import { StepConfig, FieldConfig } from '../formbuilder/field-config';
import { FormGroup, FormArray } from '@angular/forms';
import { RemoveUnderscorePipe } from '../../../core/pipes/remove-underscore.pipe';

interface SummaryItem {
  label: string;
  value?: any;
  type: string;
  items?: SummaryItem[];
  fieldName: string;
  stepTitle: string;
  rawValue?: any;
  rowIndex?: number;
}

interface SummaryGroup {
  title: string;
  items: SummaryItem[];
}

interface SummarySection {
  title: string;
  subtitle?: string;
  groups: SummaryGroup[];
  primitives: SummaryItem[];
}

@Component({
  selector: 'app-form-summary',
  templateUrl: './form-summary.component.html',
  styleUrls: ['./form-summary.component.scss'],
})
export class FormSummaryComponent {
  @Input() steps: StepConfig[] = [];
  @Input() stepGroups: FormGroup[] = [];
  @Input() formName = '';
  @Input() formSelectData: any = {};

  showFilePreview = false;
  filePreviewData: any = null;
  removeUnderscorePipe = new RemoveUnderscorePipe();


  get summarySections(): SummarySection[] {
    return this.steps
      .map((step, index) => {
        if (step.title === 'Review & Submit') {
          return null;
        }
        const group = this.stepGroups[index];
        if (!group) return null;

        const groups: SummaryGroup[] = [];
        const primitives: SummaryItem[] = [];

        step.fields.forEach((field) => {
          const control = group.get(field.name);
          if (!control) return;

          const value =
            control instanceof FormArray || control instanceof FormGroup
              ? control.getRawValue()
              : control.value;
          if (this.shouldSkipField(field, value, index)) return;

          if (Array.isArray(value)) {
            if (field.type === 'file') {
              value.forEach((file, idx) => {
                if (!file) return;

                primitives.push({
                  label: file.fileName || `${field.label || field.name} ${idx + 1}`,
                  value: null,
                  type: 'file',
                  fieldName: `${field.name}[${idx}]`,
                  stepTitle: step.title,
                  rawValue: file,
                });
              });
            } else {
              groups.push(...this.buildArrayGroups(field, value, step));
            }
          } else if (
            typeof value === 'object' &&
            value !== null &&
            field.type !== 'file' &&
            field.type !== 'date'
          ) {
            const items: SummaryItem[] = [];
            this.ObjectToItems(value, items, field.name, step.title);
            groups.push({
              title: this.formatLabel(field.label || field.name),
              items,
            });
          } else {
            primitives.push(this.buildItem(field.label || field.name, value, field.type, field.name, step.title, field));
          }
        });

        return {
          title: step.title,
          subtitle: step.subtitle,
          groups,
          primitives,
        };
      })
      .filter((s) => !!s) as SummarySection[];
  }

  private buildArrayGroups(field: FieldConfig, value: any[], step: StepConfig): SummaryGroup[] {
    const baseTitle = this.formatLabel(field.label || field.name);
    const groups: SummaryGroup[] = [];

    value.forEach((entry, idx) => {
      const items: SummaryItem[] = [];
      if (typeof entry === 'object' && entry !== null) {
        this.ObjectToItems(entry, items, field.name, step.title);
      } else {
        items.push(this.buildItem(baseTitle, entry, typeof entry, `${field.name}[${idx}]`, step.title, field));
      }
      items.forEach((i) => (i.rowIndex = idx));
      groups.push({
        title: `${baseTitle} ${idx + 1}`,
        items,
      });
    });

    return groups;
  }

  private ObjectToItems(
    obj: any,
    items: SummaryItem[],
    parentFieldName: string,
    stepTitle: string,
    parentKey: string = '',
  ): void {
    // Check if this object represents a file attachment
    if (this.isFileObject(obj)) {
      const fileName = obj.fileName || obj.attachmentId || 'File';
      items.push({
        label: fileName,
        value: null,
        type: 'file',
        fieldName: parentKey || 'file',
        stepTitle,
        rawValue: obj,
      });
      return;
    }

    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      if (value === null || value === undefined || value === '') return;

      const fieldPath = parentKey ? `${parentKey}.${key}` : key;

      // Skip individual file-related fields if parent object is a file
      if (this.isFileRelatedField(key) && this.isFileObject(obj)) {
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        // Check if this is a file object
        if (this.isFileObject(value)) {
          const fileName = value.fileName || value.attachmentId || this.formatLabel(key);
          items.push({
            label: fileName,
            value: null,
            type: 'file',
            fieldName: fieldPath,
            stepTitle,
            rawValue: value,
          });
        } else {
          const nestedItems: SummaryItem[] = [];
          this.ObjectToItems(value, nestedItems, parentFieldName, stepTitle, fieldPath);
          items.push({
            label: this.formatLabel(key),
            type: 'group',
            items: nestedItems,
            fieldName: fieldPath,
            stepTitle,
          });
        }
      } else if (Array.isArray(value)) {
        value.forEach((v, i) => {
          const detectedType = this.detectFieldType(v, key);
          items.push(this.buildItem(`${this.formatLabel(key)} ${i + 1}`, v, detectedType, `${fieldPath}[${i}]`, stepTitle));
        });
      } else {
        const detectedType = this.detectFieldType(value, key);
        items.push(this.buildItem(this.formatLabel(key), value, detectedType, fieldPath, stepTitle));
      }
    });
  }

  private isFileObject(obj: any): boolean {
    if (typeof obj !== 'object' || obj === null) return false;

    // Check if it has file-specific properties
    return !!(obj.base64 || obj.attachmentId || obj.fileName || obj.mime ||
      (obj.hasOwnProperty('attachmentId') && obj.hasOwnProperty('fileName')));
  }

  private isFileRelatedField(fieldKey: string): boolean {
    if (!fieldKey || typeof fieldKey !== 'string') return false;

    const lowerKey = fieldKey.toLowerCase();
    const fileRelatedFields = [
      'attachmentid', 'filename', 'mime', 'size', 'base64', 'islocal'
    ];

    return fileRelatedFields.includes(lowerKey);
  }

  private detectFieldType(value: any, fieldKey: string): string {
    if (typeof value === 'object' && value !== null) {
      if (value.base64 || value.attachmentId || value.fileName || value.mime) {
        return 'file';
      }
    }

    if (fieldKey && typeof fieldKey === 'string') {
      const lowerKey = fieldKey.toLowerCase();
      const filePatterns = [
        'proofofaddressid',
        'attachmentid',
        'documentid',
        'certificateid',
        'photoid',
        'imageid',
        'fileid',
        'uploadid'
      ];

      if (filePatterns.some(pattern => lowerKey.includes(pattern))) {
        return 'file';
      }

      if (lowerKey.includes('id') && (
        lowerKey.includes('proof') ||
        lowerKey.includes('document') ||
        lowerKey.includes('certificate') ||
        lowerKey.includes('attachment') ||
        lowerKey.includes('photo') ||
        lowerKey.includes('image') ||
        lowerKey.includes('file')
      )) {
        return 'file';
      }
    }

    return typeof value;
  }

  private buildItem(label: string, value: any, type: string, fieldName: string, stepTitle: string, field?: FieldConfig): SummaryItem {
    const detectedType = this.detectFieldType(value, fieldName);
    const itemType = detectedType === 'file' ? 'file' : field?.type || type;

    return {
      label: this.formatLabel(label),
      value: detectedType === 'file' ? null : this.formatValue(value, field),
      type: itemType,
      fieldName,
      stepTitle,
      rawValue: itemType === 'file' ? value : undefined,
    };
  }

  private shouldSkipField(field: FieldConfig, value: any, stepIndex?: number): boolean {
    if (field.visible === false) return true;
    if (!field.label && field.type !== 'array') return true;

    if (field.showIf && stepIndex !== undefined) {
      if (!this.isFieldVisible(field, stepIndex)) {
        return true;
      }
    }

    if (
      (value === null || value === undefined || value === '') &&
      field.type !== 'date'
    ) {
      return true;
    }

    if (field.type === 'array' && (!Array.isArray(value) || value.length === 0)) return true;

    if (field.type === 'file') {
      if (Array.isArray(value)) {
        return value.length === 0;
      }

      if (
        typeof value === 'object' &&
        value !== null &&
        !value.attachmentId &&
        !value.fileName &&
        !value.base64
      ) {
        return true;
      }
    }

    return false;
  }

  private isFieldVisible(field: FieldConfig, stepIndex: number): boolean {
    if (!field.showIf) return true;

    const group = this.stepGroups[stepIndex];
    if (!group) return true;

    if (Array.isArray(field.showIf)) {
      return field.showIf.every(condition => {
        let targetControl = group.get(condition.field);

        if (!targetControl) {
          for (let i = 0; i < this.stepGroups.length; i++) {
            if (i !== stepIndex) {
              targetControl = this.stepGroups[i].get(condition.field);
              if (targetControl) {
                break;
              }
            }
          }
        }

        if (!targetControl) {
          return true;
        }

        return targetControl.value === condition.value;
      });
    }

    let targetControl = group.get(field.showIf.field);

    if (!targetControl) {
      for (let i = 0; i < this.stepGroups.length; i++) {
        if (i !== stepIndex) {
          targetControl = this.stepGroups[i].get(field.showIf.field);
          if (targetControl) {
            break;
          }
        }
      }
    }

    if (!targetControl) {
      return true;
    }

    return targetControl.value === field.showIf.value;
  }


  private formatLabel(name: string): string {
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  private formatValue(value: any, field?: FieldConfig): any {
    if (field?.type === 'date' && value) {
      return new Date(value);
    }
    if (field?.type === 'checkbox') return value ? 'Yes' : 'No';
    if (field?.type === 'select' || field?.type === 'radio') {
      if (field.selectData && this.formSelectData && this.formSelectData[field.selectData]) {
        const option = this.formSelectData[field.selectData].find((opt: any) => opt.value === value);
        if (option) return option.label;
      }
      if (field.options) {
        const option = field.options.find((opt: any) => opt.value === value);
        if (option) return option.label;
      }
    }

    if (field?.type === 'file') {
      return null;
    }

    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object' && value !== null) return JSON.stringify(value);
    return value;
  }

  trackByIndex(index: number): number {
    return index;
  }

  get totalFields(): number {
    return this.summarySections.reduce((total, section) => total + (section.primitives.length + section.groups.length), 0);
  }

  getItemsByRow(items: SummaryItem[], row: number): SummaryItem[] {
    if (!items) return [];
    return items.filter(i => i.rowIndex === row);
  }


  getFilePreviewData(item: SummaryItem): any {
    if (!item || item.type !== 'file') return null;
    const fileValue = item.rawValue || item.value;
    if (!fileValue) return null;

    if (typeof fileValue === 'object' && fileValue !== null) {
      if (fileValue.base64) {
        const mime = fileValue.mime || 'application/octet-stream';
        return { url: `data:${mime};base64,${fileValue.base64}`, name: fileValue.fileName || 'File', type: mime, size: fileValue.size, base64: fileValue.base64, attachmentId: fileValue.attachmentId };
      }
      if (fileValue.attachmentId || fileValue.fileName) {
        return { url: '', name: fileValue.fileName || 'Uploaded file', type: fileValue.mime || 'application/octet-stream', size: fileValue.size, attachmentId: fileValue.attachmentId };
      }
    }

    return null;
  }

  openFilePreview(item: SummaryItem): void {
    const previewData = this.getFilePreviewData(item);
    if (previewData) {
      this.filePreviewData = previewData;
      this.showFilePreview = true;
    }
  }

  closeFilePreview(): void {
    this.showFilePreview = false;
    this.filePreviewData = null;
  }

  getIconForType(type: string): string {
    const map: Record<string, string> = {
      text: 'text_fields',
      email: 'email',
      phone: 'phone',
      number: 'numbers',
      textarea: 'notes',
      select: 'list',
      radio: 'radio_button_checked',
      checkbox: 'check_box',
      date: 'calendar_today',
      file: 'attach_file',
      group: 'folder',
      array: 'view_list',
    };
    return map[type] || 'label';
  }
}